import { CheckIcon } from "@heroicons/react/24/outline";
import { useMemo, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/core/components/ui/button";
import {
  SimpleSidebar,
  SimpleSidebarContent,
  SimpleSidebarLayout,
} from "@/core/components/ui/simple-sidebar";
import { Stack } from "@/core/components/ui/stack";
import {
  Tabs,
  TabsListWithIndicator,
  TabsTrigger,
} from "@/core/components/ui/tabs";
import { Typography } from "@/core/components/ui/typography";
import { useBusiness } from "@/core/business";
import { ApAutomatedTab } from "./automated-tab";
import { ApEmailDetail, ApEmailsTab } from "./emails-tab";
import { ApInvoiceDetail } from "./invoice-detail";
import { ApInvoicesTab } from "./invoices-tab";
import { useBulkApproveAgentActions, useGetReviewQueue } from "./query";
import { ApReceiptDetail } from "./receipt-detail";
import { ApReceiptsTab } from "./receipts-tab";
import { ApCombinedInvoicesTab } from "./review-tab";
import { UploadInvoicesButton } from "./upload-invoices-button";
import { useReceiptsEnabled } from "./use-receipts-enabled";
import {
  AP_TAB,
  type ApTabValue,
  flattenPages,
  invoiceReviewPath,
  isHighConfidenceApprovable,
  parseApTab,
  receiptReviewPath,
} from "./utils";
import { ApVendorDetail, ApVendorsTab } from "./vendors-tab";

const SELECTED_OBJECT_KIND = {
  email: "email",
  invoice: "invoice",
  receipt: "receipt",
  vendor: "vendor",
} as const;

type SelectedObjectKind =
  (typeof SELECTED_OBJECT_KIND)[keyof typeof SELECTED_OBJECT_KIND];

type Selection = { kind: SelectedObjectKind; id: string } | null;

const selectedIdFor = (
  selected: Selection,
  kind: SelectedObjectKind,
): string | null => (selected?.kind === kind ? selected.id : null);

const AccountsPayable = () => {
  const { businessId } = useBusiness();
  const navigate = useNavigate();
  const location = useLocation();
  // The URL owns the active tab, so a shared link can be tab-specific, and the
  // invoice detail ("review") page sends you back to your original tab.
  const [searchParams, setSearchParams] = useSearchParams();
  const receiptsEnabled = useReceiptsEnabled();
  const requestedTab = parseApTab(searchParams.get("tab")) ?? AP_TAB.review;
  // A receipts link for a business with receipts off lands on Review.
  const activeTab =
    requestedTab === AP_TAB.receipts && !receiptsEnabled
      ? AP_TAB.review
      : requestedTab;
  const [selected, setSelected] = useState<Selection>(null);

  // Shares the Review tab's queue query, so this does not add a request.
  const { data: reviewQueue, hasNextPage } = useGetReviewQueue(businessId);
  const bulkApprove = useBulkApproveAgentActions(businessId);
  const highConfidenceIds = useMemo(
    () =>
      flattenPages(reviewQueue?.pages)
        .flatMap(({ last_completed_agent_run: { actions } }) =>
          actions.filter((action) =>
            isHighConfidenceApprovable(action, actions),
          ),
        )
        .map((action) => action.id),
    [reviewQueue],
  );
  // The API pages without a total, so this counts what has loaded; the "+"
  // says so rather than implying the queue is this short.
  const reviewCount = flattenPages(reviewQueue?.pages).length;

  const closeSidebar = () => setSelected(null);
  const changeTab = (v: ApTabValue) => {
    setSelected(null);
    // Replace, so tab switching doesn't fill the back stack.
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set("tab", v);
        return next;
      },
      { replace: true },
    );
  };
  const selectObject = (kind: SelectedObjectKind, id: string) =>
    setSelected({ kind, id });
  const openInvoiceReview = (id: string) =>
    navigate(invoiceReviewPath(id, activeTab), {
      state: {
        invoiceListReturnContext: {
          businessId,
          listPath: location.pathname + location.search + location.hash,
        },
      },
    });
  const openReceiptReview = (id: string) =>
    navigate(receiptReviewPath(id, activeTab));

  return (
    <SimpleSidebarLayout
      open={!!selected}
      onOpenChange={(open) => !open && setSelected(null)}
    >
      <SimpleSidebarContent removeScrollArea>
        {/* align="stretch": Stack defaults to items-start, which shrink-wraps
            any child without its own w-full — the tab strip and the queue then
            sat narrower than this header. */}
        {/* gap="none": Stack's default gap-4 stacked on top of the header's
            own padding, putting 32px between the title row and the tabs. */}
        <Stack
          fullWidth
          gap="none"
          align="stretch"
          // pt-6 mirrors Payroll's header breathing room below the app nav; the
          // title row's own top padding is dropped so the two don't stack.
          className="mx-auto flex h-full max-w-[1680px] flex-col px-8 pb-6 pt-6"
        >
          <Stack
            direction="row"
            align="center"
            justify="between"
            fullWidth
            className="flex-none pb-2"
          >
            <Typography variant="titleW3">Accounts Payable</Typography>
            {/* Page + review actions share the header's right edge. Bulk approve
                sits left of the persistent Upload button so Upload never shifts
                as it appears/disappears with the active tab. */}
            <Stack
              direction="row"
              align="center"
              gap="sm"
              className="flex-none"
            >
              {activeTab === AP_TAB.review && highConfidenceIds.length > 0 && (
                <Button
                  size="sm"
                  variant="secondary"
                  className="flex-none"
                  loading={bulkApprove.isPending}
                  leftIcon={<CheckIcon />}
                  title={
                    hasNextPage
                      ? "Applies to the invoices loaded so far"
                      : undefined
                  }
                  onClick={() => bulkApprove.mutate(highConfidenceIds)}
                >
                  Approve {highConfidenceIds.length} high-confidence
                </Button>
              )}
              <UploadInvoicesButton
                businessId={businessId}
                // Receipts are uploaded from their own tab; everywhere else
                // the button uploads invoices, as before.
                documentType={
                  activeTab === AP_TAB.receipts ? "receipt" : "invoice"
                }
              />
            </Stack>
          </Stack>

          <Tabs
            value={activeTab}
            onValueChange={(v) => changeTab(v as ApTabValue)}
            className="flex min-h-0 flex-1 flex-col"
          >
            {/* The tab strip is a full-width block so its underline rule runs
                edge to edge, not just under the tabs. The list inside stays
                left-aligned and the active-tab indicator rides with it. */}
            <TabsListWithIndicator
              size="small"
              className="justify-start"
              containerClassName="w-full flex-none"
            >
              <TabsTrigger value={AP_TAB.review}>
                <span className="inline-flex items-center gap-1.5">
                  Review
                  {reviewCount > 0 && (
                    <span className="rounded-full bg-gold-20 px-1.5 text-sm font-semibold leading-[18px] text-primary-text">
                      {reviewCount}
                      {hasNextPage ? "+" : ""}
                    </span>
                  )}
                </span>
              </TabsTrigger>
              <TabsTrigger value={AP_TAB.automated}>Automated</TabsTrigger>
              <TabsTrigger value={AP_TAB.invoices}>Invoices</TabsTrigger>
              {receiptsEnabled && (
                <TabsTrigger value={AP_TAB.receipts}>Receipts</TabsTrigger>
              )}
              <TabsTrigger value={AP_TAB.emails}>Emails</TabsTrigger>
              <TabsTrigger value={AP_TAB.vendors}>Vendors</TabsTrigger>
            </TabsListWithIndicator>

            {/* Review and Automated stack one card per invoice on the page
                  ground; Invoices, Emails and Vendors each own their table
                  card, so the Invoices controls sit on the ground too. */}
            {activeTab === AP_TAB.review ? (
              <Stack
                gap="none"
                fullWidth
                className="min-h-0 flex-1 overflow-hidden"
              >
                <ApCombinedInvoicesTab
                  businessId={businessId}
                  bulkApproving={bulkApprove.isPending}
                  onBulkApprove={bulkApprove.mutate}
                  onBrowseInvoices={() => changeTab(AP_TAB.invoices)}
                />
              </Stack>
            ) : activeTab === AP_TAB.automated ? (
              <Stack
                gap="none"
                fullWidth
                className="min-h-0 flex-1 overflow-hidden"
              >
                <ApAutomatedTab
                  businessId={businessId}
                  onSelectInvoice={openInvoiceReview}
                />
              </Stack>
            ) : activeTab === AP_TAB.invoices ? (
              // No overflow clip here: it would cut off the filter controls'
              // focus rings. The table card clips its own content.
              <Stack gap="none" fullWidth className="min-h-0 flex-1">
                <ApInvoicesTab
                  key={businessId}
                  businessId={businessId}
                  selectedId={selectedIdFor(
                    selected,
                    SELECTED_OBJECT_KIND.invoice,
                  )}
                  onSelect={(id) =>
                    selectObject(SELECTED_OBJECT_KIND.invoice, id)
                  }
                />
              </Stack>
            ) : activeTab === AP_TAB.receipts ? (
              <Stack gap="none" fullWidth className="min-h-0 flex-1">
                <ApReceiptsTab
                  businessId={businessId}
                  selectedId={selectedIdFor(
                    selected,
                    SELECTED_OBJECT_KIND.receipt,
                  )}
                  onSelect={(id) =>
                    selectObject(SELECTED_OBJECT_KIND.receipt, id)
                  }
                />
              </Stack>
            ) : activeTab === AP_TAB.emails ? (
              <Stack gap="none" fullWidth className="min-h-0 flex-1">
                <ApEmailsTab
                  businessId={businessId}
                  selectedId={selectedIdFor(
                    selected,
                    SELECTED_OBJECT_KIND.email,
                  )}
                  onSelect={(id) =>
                    selectObject(SELECTED_OBJECT_KIND.email, id)
                  }
                />
              </Stack>
            ) : (
              <Stack gap="none" fullWidth className="min-h-0 flex-1">
                <ApVendorsTab
                  businessId={businessId}
                  selectedId={selectedIdFor(
                    selected,
                    SELECTED_OBJECT_KIND.vendor,
                  )}
                  onSelect={(id) =>
                    selectObject(SELECTED_OBJECT_KIND.vendor, id)
                  }
                />
              </Stack>
            )}
          </Tabs>
        </Stack>
      </SimpleSidebarContent>

      <SimpleSidebar>
        {selected?.kind === SELECTED_OBJECT_KIND.email && (
          <ApEmailDetail
            emailId={selected.id}
            businessId={businessId}
            onClose={closeSidebar}
          />
        )}
        {selected?.kind === SELECTED_OBJECT_KIND.vendor && (
          <ApVendorDetail
            vendorId={selected.id}
            businessId={businessId}
            onClose={closeSidebar}
          />
        )}
        {selected?.kind === SELECTED_OBJECT_KIND.receipt && (
          <ApReceiptDetail
            receiptId={selected.id}
            businessId={businessId}
            onClose={closeSidebar}
            onViewAgentReview={(id) => {
              setSelected(null);
              openReceiptReview(id);
            }}
          />
        )}
        {selected?.kind === SELECTED_OBJECT_KIND.invoice && (
          <ApInvoiceDetail
            invoiceId={selected.id}
            businessId={businessId}
            onClose={closeSidebar}
            onViewAgentReview={(id) => {
              setSelected(null);
              openInvoiceReview(id);
            }}
          />
        )}
      </SimpleSidebar>
    </SimpleSidebarLayout>
  );
};

export default AccountsPayable;
